import React from "react";
import { Box } from "@mui/material";
import { certificatePageSize, isDarkPaper, certificatePaperColour } from "../../../domain/certificates/certificateTemplates";
import { CentredLayout, type CertificateDesign } from "./certificateParts";
import { starPoints } from "./ornamentShapes";

type PurpleStyle = "purple-botanical" | "modern-purple" | "elegant-purple" | "playful-purple" | "royal-purple" | "modern-angles-purple";

/** Vector ornaments stay sharp in previews and downloaded certificates. */
function purpleDesign(style: PurpleStyle): CertificateDesign {
  return {
    bodyFont: "raleway",
    Layout: ({ template, text, orientation }) => {
      const { width: w, height: h } = certificatePageSize(orientation);
      const { primaryColour: primary, accentColour: accent } = template;
      const royal = style === "royal-purple";
      const ink = isDarkPaper(certificatePaperColour(template)) ? undefined : primary;
      const waves = style === "purple-botanical" || style === "playful-purple" || royal;
      return (
        <CentredLayout
          template={template} text={text} orientation={orientation}
          inset={{ landscape: "76px 170px 70px", portrait: "120px 90px 100px" }}
          lettering={{ nameColour: ink, titleColour: primary }}
          frame={
            <Box component="svg" viewBox={`0 0 ${w} ${h}`} aria-hidden="true"
              sx={{ position: "absolute", inset: 0, width: w, height: h }}>
              {waves && <>
                {[0, 1, 2].map((i) => <g key={i} fill={i === 1 ? primary : accent} opacity={royal ? 0.22 : 0.16 + i * 0.12}>
                  <path d={`M0 0 H${400 - i * 75} C190 50 150 150 0 ${280 - i * 50} Z`} />
                  <path d={`M0 ${h} V${h - 160 + i * 30} C220 ${h - 120} 220 ${h + 60} 480 ${h} Z`} />
                  <path d={`M${w} ${h} H${w - 420 + i * 60} C${w - 140} ${h - 80} ${w - 200} ${h - 240} ${w} ${h - 300 + i * 55} Z`} />
                </g>)}
              </>}
              {style === "purple-botanical" && ["translate(18 390)", `translate(${w - 18} 250) rotate(180)`].map(transform =>
                <g key={transform} transform={transform} stroke={primary} fill={accent} opacity={0.8}>
                  <path d="M0 200 Q100 60 60 -180" fill="none" strokeWidth={3} />
                  {Array.from({ length: 7 }, (_, i) => <g key={i} transform={`translate(${55 + Math.sin(i) * 12} ${140 - i * 43})`}>
                    <path d="M0 0 Q-70 -12 -60 -65 Q-15 -55 0 0" />
                    <path d="M0 0 Q65 -15 65 -60 Q15 -50 0 0" />
                  </g>)}
                </g>)}
              {style === "modern-purple" && <>
                {[[0, 0, 185], [w, 0, 230], [0, h, 175], [w - 35, h - 190, 130]].map(([x, y, r], i) =>
                  <circle key={i} cx={x} cy={y} r={r} fill={accent} opacity={i % 2 ? 0.3 : 0.75} />)}
                <circle cx={w - 60} cy={h / 2} r={22} fill={accent} />
                {[0, 1].map(side => <g key={side} fill={accent} opacity={0.4}>
                  {Array.from({ length: 20 }, (_, i) => <circle key={i} cx={side ? w - 100 + (i % 4) * 16 : 35 + (i % 4) * 16} cy={180 + Math.floor(i / 4) * 20} r={3} />)}
                </g>)}
              </>}
              {style === "playful-purple" && [[w - 140, 90, 48], [w - 80, 240, 24], [80, h / 2, 30], [175, h - 190, 25]].map(([x, y, r], i) =>
                <polygon key={i} transform={`translate(${x} ${y})`} points={starPoints(5, r, r * 0.43)} fill="none" stroke={primary} strokeWidth={4} />)}
              {(style === "elegant-purple" || royal) && <>
                {[25, 34].map(inset => <rect key={inset} x={inset} y={inset} width={w - inset * 2} height={h - inset * 2} fill="none" stroke={royal ? accent : primary} strokeWidth={inset === 25 ? 2 : 1} opacity={0.8} />)}
                {[`translate(25 25)`, `translate(${w - 25} 25) scale(-1 1)`, `translate(25 ${h - 25}) scale(1 -1)`, `translate(${w - 25} ${h - 25}) scale(-1 -1)`].map(transform =>
                  <path key={transform} transform={transform} d="M0 45 Q45 45 45 0 M0 12 H12 V0" fill="none" stroke={royal ? accent : primary} strokeWidth={2} />)}
              </>}
              {style === "elegant-purple" && <>
                <path d={`M${w - 205} 0 H${w - 85} V360 L${w - 145} 315 L${w - 205} 360 Z`} fill={primary} />
                <g transform={`translate(${w - 145} 200)`}>
                  <polygon points={starPoints(24, 80, 73)} fill={accent} />
                  <circle r={64} fill="none" stroke={primary} strokeWidth={2} />
                  <polygon points={starPoints(5, 42, 19)} fill={primary} opacity={0.65} />
                </g>
                <path d={`M${w - 260} ${h} L${w} ${h - 260}`} stroke={accent} strokeWidth={6} />
              </>}
              {style === "modern-angles-purple" && ["", `translate(${w} ${h}) rotate(180)`].map(transform =>
                <g key={transform} transform={transform}>
                  <polygon points="0,0 290,0 0,260" fill={primary} />
                  <polygon points={`${w - 260},0 ${w},0 ${w},260`} fill={accent} opacity={0.4} />
                  <polygon points={`${w - 190},0 ${w - 75},0 ${w - 190},120 ${w - 250},60`} fill={accent} />
                  <path d="M0 320 L340 0" stroke={primary} strokeWidth={2} />
                </g>)}
            </Box>
          }
        />
      );
    },
  };
}

export const purpleDesigns: Record<PurpleStyle, CertificateDesign> = {
  "purple-botanical": purpleDesign("purple-botanical"),
  "modern-purple": purpleDesign("modern-purple"),
  "elegant-purple": purpleDesign("elegant-purple"),
  "playful-purple": purpleDesign("playful-purple"),
  "royal-purple": purpleDesign("royal-purple"),
  "modern-angles-purple": purpleDesign("modern-angles-purple"),
};
